import { HttpClient } from '@angular/common/http';
import { computed, inject, Service } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { catchError, distinctUntilChanged, filter, map, of, startWith, switchMap } from 'rxjs';

import {
  APP_CONFIG,
  IAppConfig,
  IBrandingSettings,
  IRoomLoginInfo,
  LOCATION, ROOM_NAME_PARAM,
} from '@ideals/types';
import { queryParamsToObject } from '@ideals/utils';

export const BASE_ROOMS_API_BY_NAME = '/api/ca/branding-settings/room';
export const THEME_COLOR_TO_CLASS: Record<string, string> = {
  '#ef5350': 'red',
  '#ec407a': 'pink',
  '#ab47bc': 'purple',
  '#9575cd': 'deep-purple',
  '#5c6bc0': 'indigo',
  '#4055b3': 'primary-indigo',
  '#4862d3': 'royal-indigo',
  '#536dfe': 'indigo-modern',
  '#6191ca': 'shark-bay',
  '#358ceb': 'deep-blue',
  '#42a5f5': 'blue',
  '#29b6f6': 'light-blue',
  '#26c6da': 'cyan',
  '#4894b0': 'sea-wave',
  '#26a69a': 'teal',
  '#009688': 'teal-modern',
  '#4caf50': 'hard-green',
  '#42a85f': 'ideals-corp',
  '#66bb6a': 'green',
  '#8bc34a': 'light-green',
  '#f7a738': 'sun',
  '#ff7043': 'deep-orange',
  '#f4640c': 'orange',
  '#e96a48': 'brick',
  '#8d6e63': 'brown',
  '#607d8b': 'blue-grey',
  '#616161': 'grey',
  '#535353': 'matterhorn',
  '#3e3e3e': 'noir',
  '#891157': 'wine',
};
export const DEFAULT_THEME_CLASS = 'ideals-corp';

const RETURN_URL_QUERY_PARAM = 'ReturnUrl';

export function getThemeColor(color: string): string {
  return THEME_COLOR_TO_CLASS[color] || DEFAULT_THEME_CLASS;
}

@Service()
export class BrandingService {
  readonly #appConfig = inject<IAppConfig>(APP_CONFIG);
  readonly #httpClient = inject(HttpClient);
  readonly #location = inject<Location>(LOCATION);
  readonly #router = inject(Router);
  readonly #settings = toSignal(
    this.#router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        startWith(null),
        map(() => this.#getRoomName()),
        distinctUntilChanged(),
        switchMap((roomName) => roomName
          ? this.#httpClient
            .get<IBrandingSettings>(`${this.#appConfig.appUrl}${BASE_ROOMS_API_BY_NAME}`, { params: { roomName } })
            .pipe(catchError(() => of({} as IBrandingSettings)))
          : of({} as IBrandingSettings)),
      ),
    { initialValue: null as IBrandingSettings | null },
  );

  readonly applicationBackground = computed(() => this.#settings()?.welcome?.location ?? '');
  readonly applicationThemeClassName = computed(() => `theme-${getThemeColor(this.#settings()?.color?.main)}`);
  readonly logotypeWithRoomName = computed<IRoomLoginInfo | null>(() => {
    const settings = this.#settings();

    return settings
      ? {
        logoHeight: settings.logoV3?.height,
        logotypeUrl: settings.logoV3?.location,
        roomTitle: settings.displayName,
      }
      : null;
  });

  readonly logotypeWithRoomName$ = toObservable(this.logotypeWithRoomName);

  #getRoomName(): string {
    const params = queryParamsToObject(this.#location.search);

    if (params.has(ROOM_NAME_PARAM)) {
      return params.get(ROOM_NAME_PARAM);
    }

    if (params.has(RETURN_URL_QUERY_PARAM)) {
      const returnUrl = params.get(RETURN_URL_QUERY_PARAM);
      const returnUrlParams = queryParamsToObject(returnUrl);

      if (returnUrlParams.has(ROOM_NAME_PARAM)) {
        return returnUrlParams.get(ROOM_NAME_PARAM);
      }
    }

    return undefined;
  }
}
